import React, { FC, useState, useEffect } from "react";
import {
  Phase,
  AccruedFee,
  MatterFilter,
  ExternalMatter,
  BaseAccruedFee,
  RelevantMatter,
} from "@design-system";
import { RelevantMatterTabs, MatterTitleScreenTabKey } from "@design-system";
import {
  MATTER_ASSUMPTIONS_QUERY,
  MATTER_EXCLUSIONS_QUERY,
  FIND_MATTER,
  FILTERED_CLIENTS_QUERY,
  FILTER_MATTERS_QUERY,
} from "~/graphql/query";

import { COPY_SCOPE, COPY_RESOURCING, COPY_PRICING, UPDATE_MATTER } from "~/graphql/mutation";
import { useMutation } from "~/hooks";
import produce from "immer";
import { useRelevantMatterSearch, useRelevantExternalMatterSearch } from "~/hooks/matters";
import { updateFindMatterCache } from "~/graphql/query/find-matter-query";
import { useApolloClient } from "@apollo/client";
import { FindMatterQuery } from "~/gql/graphql";
import { TOGGLE_FAVOURITE } from "~/graphql/mutation/toggle-favourite";
import { FIND_RELEVANT_MATTERS_QUERY } from "~/graphql/query";

type HistoricalMattersContainerProps = {
  matterId: number;
  matterTitle: string;
  clientName: string;
  aiGeneratedMatterSummary: string | null;
  orgUnitId?: number;
  phases: Phase[];
  currencyId: number;
  setTopDownRefreshTrigger: React.Dispatch<React.SetStateAction<number>>;
  pinnedMatterIds: number[];
};

const INITIAL_MATTER_FILTER: MatterFilter = {
  title: null,
  client: null,
  orgUnit: null,
  status: null,
  updatedAt: null,
  orderBy: null,
  createdBy: null,
  currencyId: null,
  feeEarner: null,
  someTimeEntries: true,
  linked: null,
};

export const HistoricalMattersContainer: FC<HistoricalMattersContainerProps> = ({
  matterId,
  matterTitle,
  clientName,
  aiGeneratedMatterSummary,
  orgUnitId,
  phases,
  currencyId,
  setTopDownRefreshTrigger,
  pinnedMatterIds,
}) => {
  const [activeTab, setActiveTab] = useState<MatterTitleScreenTabKey>(
    MatterTitleScreenTabKey.HistoricalMatters
  );
  const [selectedMatter, setSelectedMatter] = useState<RelevantMatter | null>(null);
  const [selectedAccruedFees, setSelectedAccruedFees] = useState<AccruedFee | null>(null);
  const [selectedExternalMatter, setSelectedExternalMatter] = useState<ExternalMatter | null>(null);
  const [selectedExternalAccruedFees, setSelectedExternalAccruedFees] =
    useState<BaseAccruedFee | null>(null);
  const [searchText, setSearchText] = useState<string>("");
  const [additionalSearchText, setAdditionalSearchText] = useState<string>("");
  const [matterFilter, setMatterFilter] = useState<MatterFilter>({
    ...INITIAL_MATTER_FILTER,
    orgUnit: orgUnitId ? orgUnitId : null,
    ...(currencyId ? { currencyId: [currencyId] } : {}),
  });

  useEffect(() => {
    setMatterFilter((prev) => ({
      ...prev,
      orgUnit: orgUnitId ? orgUnitId : null,
      currencyId: currencyId ? [currencyId] : [],
    }));
  }, [orgUnitId, currencyId]);

  const client = useApolloClient();

  const {
    historicalData,
    historicalLoading: historicalMattersLoading,
    triggerSearch: triggerHistoricalSearch,
  } = useRelevantMatterSearch(searchText, clientName, matterFilter, {
    additionalSearchText: additionalSearchText?.trim() || undefined,
    autoRetry: true,
    fetchTemplates: false,
    excludeMatterId: matterId,
  });

  const { externalMattersData, externalMattersLoading } = useRelevantExternalMatterSearch(
    searchText,
    clientName,
    matterFilter,
    {
      additionalSearchText: additionalSearchText?.trim() || undefined,
      autoRetry: true,
      unlinkedOnly: true,
    }
  );

  const [copyScope] = useMutation(COPY_SCOPE, {
    refetchQueries: [MATTER_ASSUMPTIONS_QUERY, MATTER_EXCLUSIONS_QUERY],
  });
  const [copyResourcing] = useMutation(COPY_RESOURCING);
  const [copyPricing] = useMutation(COPY_PRICING, {
    update(cache, { data }) {
      const copyPricing = data?.copyPricing;
      if (copyPricing) {
        cache.updateQuery({ query: FIND_MATTER, variables: { id: matterId } }, (currentData) => {
          if (currentData?.findMatter) {
            return produce(currentData, (draft) => {
              draft.findMatter.teamMemberships = copyPricing.teamMemberships;
              draft.findMatter.phases = copyPricing.phases;
              draft.findMatter.work = copyPricing.work;
            });
          }
        });
      }
    },
    refetchQueries: [
      {
        query: FIND_MATTER,
        variables: { id: matterId },
      },
    ],
    onCompleted: () => {
      setTopDownRefreshTrigger((prev) => prev + 1);
    },
  });

  const [updateMatter] = useMutation(UPDATE_MATTER);
  const [toggleFavourite] = useMutation(TOGGLE_FAVOURITE);

  useEffect(() => {
    const text = `Matter Title: ${matterTitle}\n\n${phases
      ?.map((phase) => {
        return `  - Phase: ${phase.title}\n    Tasks:\n${phase.tasks
          ?.map((task) => `      - ${task.title}\n`)
          .join("")}`;
      })
      .join("\n")}`;
    setSearchText(text + (aiGeneratedMatterSummary ? `\n\n${aiGeneratedMatterSummary}` : ""));
  }, [matterTitle, phases]);

  const handleSelectedMatterChange = (matter: RelevantMatter | null) => {
    setSelectedMatter(matter);
    setSelectedAccruedFees(
      historicalData?.findRelevantMatters?.accruedFees?.find(
        (fee) => fee.matterId === matter?.id
      ) || null
    );
    setSelectedExternalMatter(null);
    setSelectedExternalAccruedFees(null);
  };

  const handleSelectedExternalMatterChange = (matter: ExternalMatter | null) => {
    setSelectedExternalMatter(matter);
    setSelectedExternalAccruedFees(
      externalMattersData?.findRelevantExternalMatters?.accruedFees?.find(
        (fee) => fee.externalMatterId === matter?.id
      ) || null
    );
    setSelectedMatter(null);
    setSelectedAccruedFees(null);
  };

  const handleFilterChange = (newFilters: MatterFilter) => {
    setMatterFilter(newFilters);
    // Filters changed, but we don't need to manually trigger the search
    // as it will be triggered by the useEffect in the hooks when the filter state changes
  };

  const handlePinToMatter = (matterToPinId: number) => {
    const cache = client.cache;
    updateFindMatterCache(cache, matterId, (findMatter: any) => {
      const currentPinnedMatters = findMatter.pinnedMatters || [];
      const alreadyPinned = currentPinnedMatters.some(
        (matter: { id: number }) => matter.id === matterToPinId
      );

      if (alreadyPinned) {
        updateMatter({
          variables: { id: matterToPinId, data: { pinnedTo: { disconnect: [{ id: matterId }] } } },
          update: (mutationCache) => {
            updateFindMatterCache(mutationCache, matterId, (innerFindMatter: any) => {
              const innerCurrentPinnedMatters = innerFindMatter.pinnedMatters || [];
              return {
                pinnedMatters: innerCurrentPinnedMatters.filter(
                  (matter: { id: number }) => matter.id !== matterToPinId
                ),
              } as Partial<FindMatterQuery>;
            });
          },
          refetchQueries: [
            {
              query: FILTERED_CLIENTS_QUERY,
              variables: {
                input: {
                  filter: {
                    pinnedToId: matterId,
                  },
                  limit: 100,
                },
              },
            },
          ],
        });
        return {} as Partial<FindMatterQuery>;
      } else {
        updateMatter({
          variables: { id: matterToPinId, data: { pinnedTo: { connect: [{ id: matterId }] } } },
          update: (mutationCache) => {
            updateFindMatterCache(mutationCache, matterId, (innerFindMatter: any) => {
              const innerCurrentPinnedMatters = innerFindMatter.pinnedMatters || [];
              return {
                pinnedMatters: [
                  ...innerCurrentPinnedMatters,
                  { id: matterToPinId, __typename: "Matter" },
                ],
              } as Partial<FindMatterQuery>;
            });
          },
          refetchQueries: [
            {
              query: FILTER_MATTERS_QUERY,
              variables: {
                input: {
                  filter: {
                    pinnedToId: matterId,
                  },
                  limit: 100,
                },
              },
            },
          ],
        });
        return {} as Partial<FindMatterQuery>;
      }
    });
  };

  const handleFavouriteMatter = (matterId: number) => {
    const matter = historicalData?.findRelevantMatters?.matters?.find(
      (matter) => matter.id === matterId
    );
    if (!matter) return;

    const isFavourite = matter.isFavourite;

    // Create an updated copy with the changed favorite status
    const updatedMatters =
      historicalData?.findRelevantMatters?.matters.map((m) =>
        m.id === matterId ? { ...m, isFavourite: !isFavourite } : m
      ) || [];

    // Create the complete updated data structure
    const updatedData = {
      ...historicalData,
      findRelevantMatters: {
        ...historicalData?.findRelevantMatters,
        matters: updatedMatters,
      },
    };

    // Write directly to the Apollo cache
    client.writeQuery({
      query: FIND_RELEVANT_MATTERS_QUERY,
      variables: {
        text: searchText,
        clientName,
        filter: {
          template: false,
          organisationalUnitId: matterFilter.orgUnit,
          excludeMatterId: matterId,
        },
        fetchAccruedFees: true,
        additionalSearchText: additionalSearchText?.trim() || null,
      },
      data: updatedData,
    });

    // Send the mutation to update the server
    toggleFavourite({
      variables: { id: matterId, favourite: !isFavourite },
      onError: () => {
        triggerHistoricalSearch();
      },
    });
  };

  return (
    <div style={{ maxWidth: "90vw", marginLeft: "20px" }}>
      <RelevantMatterTabs
        createMode={false}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        selectedMatter={selectedMatter}
        selectedAccruedFees={selectedAccruedFees}
        handleSelectedMatterChange={handleSelectedMatterChange}
        selectedExternalMatter={selectedExternalMatter}
        selectedExternalAccruedFees={selectedExternalAccruedFees}
        handleSelectedExternalMatterChange={handleSelectedExternalMatterChange}
        templateMatters={undefined}
        historicalMatters={historicalData}
        historicalComparisonLoading={externalMattersLoading || historicalMattersLoading}
        externalMatters={externalMattersData}
        additionalSearchText={additionalSearchText}
        setAdditionalSearchText={setAdditionalSearchText}
        templateMattersLoading={false}
        historicalMattersLoading={historicalMattersLoading}
        maxHeight="75vh"
        onCopyScope={
          selectedMatter?.id
            ? () => {
                copyScope({
                  variables: {
                    scopeMatterId: selectedMatter.id,
                    targetMatterId: matterId,
                  },
                });
              }
            : undefined
        }
        onCopyResourcing={
          selectedMatter?.id
            ? (rateReplacements) => {
                copyResourcing({
                  variables: {
                    resourcingMatterId: selectedMatter?.id,
                    targetMatterId: matterId,
                    rateReplacements,
                  },
                });
              }
            : undefined
        }
        onCopyPricing={
          selectedMatter?.id
            ? (rateReplacements) => {
                copyPricing({
                  variables: {
                    pricingMatterId: selectedMatter?.id,
                    targetMatterId: matterId,
                    copyScope: false,
                    copyResourcing: false,
                    rateReplacements,
                  },
                });
              }
            : undefined
        }
        onCopyAll={
          selectedMatter?.id
            ? (rateReplacements) => {
                copyPricing({
                  variables: {
                    pricingMatterId: selectedMatter?.id,
                    targetMatterId: matterId,
                    copyScope: true,
                    copyResourcing: true,
                    rateReplacements,
                  },
                });
              }
            : undefined
        }
        matterFilter={matterFilter}
        handleFilterChange={handleFilterChange}
        allowChangeCurrency={false}
        onPinToMatter={handlePinToMatter}
        pinnedMatterIds={pinnedMatterIds}
        onFavouriteMatter={handleFavouriteMatter}
      />
    </div>
  );
};
